import type { EmailText } from '@repo/sdk/gen'
import { Badge, type BadgeVariant } from '@repo/ui/badge'
import { DataTableColumnSettings } from '@repo/ui/components/data-table/column-settings'
import {
	DataTable,
	useDataTableColumnState
} from '@repo/ui/components/data-table/data-table'
import type { DataTableColumn } from '@repo/ui/components/data-table/types'
import { ListLayout } from '@repo/ui/components/list-layout'
import { formatLocaleDate } from '@repo/ui/dates'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { type TKey, translateKey, useTranslation } from '@/lib/i18n'
import { LegalTextsNote } from './-components/legal-texts-note'
import { emailTextsQueryOptions } from './-query/query-options'

type Row = EmailText & { id: string }

type Audience = EmailText['audience']

const EMPTY: Row[] = []

const AUDIENCE_LABEL_KEYS = {
	student: 'admin-email-texts:audiences.student',
	manager: 'admin-email-texts:audiences.manager',
	admin: 'admin-email-texts:audiences.admin'
} as const satisfies Record<Audience, TKey>

const AUDIENCE_TONE = {
	student: 'info',
	manager: 'secondary',
	admin: 'outline'
} as const satisfies Record<Audience, BadgeVariant>

/** The readers in the order of the catalogue: the student first, the general admin last. */
const AUDIENCE_ORDER = Object.keys(AUDIENCE_LABEL_KEYS)

export const Route = createFileRoute('/(private)/email-texts/')({
	component: EmailTextsPage
})

/** "Testi delle email" (FFD 3.8): the emails without legal value, by name and by who they go to. */
function EmailTextsPage() {
	const { t } = useTranslation()
	const query = useQuery(emailTextsQueryOptions())
	const rows = query.data
		?.map((text) => ({ ...text, id: text.code }))
		.sort(
			(a, b) =>
				AUDIENCE_ORDER.indexOf(a.audience) -
					AUDIENCE_ORDER.indexOf(b.audience) ||
				a.code.localeCompare(b.code, 'en', { numeric: true })
		)

	const columns: DataTableColumn<Row>[] = [
		{
			id: 'name',
			header: t('admin-email-texts:columns.email'),
			pinned: 'left',
			hideable: false,
			cell: (row) => (
				<Link
					className='block max-w-full truncate font-medium hover:underline'
					params={{ code: row.code }}
					to='/email-texts/$code'
				>
					{translateKey(`admin-email-texts:emails.${row.code}`)}
				</Link>
			),
			minWidth: 280,
			flex: 3
		},
		{
			id: 'audience',
			header: t('admin-email-texts:columns.audience'),
			cell: (row) => (
				<Badge variant={AUDIENCE_TONE[row.audience]}>
					{t(AUDIENCE_LABEL_KEYS[row.audience])}
				</Badge>
			),
			minWidth: 150,
			flex: 1
		},
		{
			id: 'code',
			header: t('admin-email-texts:columns.code'),
			cell: (row) => (
				<span className='text-muted-foreground tabular-nums'>
					{row.code}
				</span>
			),
			minWidth: 90,
			flex: 1
		},
		...(['it', 'en'] as const).map((language) => ({
			id: language,
			header: t(`admin-email-texts:columns.${language}`),
			cell: (row: Row) => {
				const current = row.locales.find(
					(locale) => locale.locale === language
				)?.current
				return (
					<span className='tabular-nums'>
						{current?.createdAt
							? t('admin-email-texts:version_on', {
									version: current.version,
									date: formatLocaleDate(
										current.createdAt,
										'P'
									)
								})
							: t('admin-email-texts:default_text')}
					</span>
				)
			},
			minWidth: 190,
			flex: 1
		}))
	]

	const columnsState = useDataTableColumnState(columns)

	return (
		<ListLayout
			controlsSlot={
				<>
					<LegalTextsNote />
					<DataTableColumnSettings
						columns={columns}
						columnsState={columnsState}
					/>
				</>
			}
			listSlot={
				<DataTable
					columns={columns}
					columnsState={columnsState}
					data={rows ?? EMPTY}
					state={{
						loading: query.isPending,
						error: query.isError
							? t('admin-email-texts:error')
							: false
					}}
				/>
			}
			title={t('admin-email-texts:page_title')}
		/>
	)
}
